import React, { ForwardRefExoticComponent, SVGProps } from 'react'

import { ChevronDown } from '@factorialco/f0-react/icons/app'
import { FormsElementTypeEnum } from 'generated/resources/datatable.graphql'

import i18n from 'modules/core/lib/i18n'

import DropdownElementQuestion from 'modules/forms/components/QuestionComponent/DropdownElementQuestion'
import ChoiceElementEditor from 'modules/forms/components/editor/ChoiceElementEditor/ChoiceElementEditor'
import { Element } from 'modules/forms/lib/Element'
import { Answer, Options, elementParam } from 'modules/forms/lib/types'

/**
 * DropdownElement class for elements with a single selectable option via dropdown.
 * Extends the base Element class.
 */
export class DropdownElement extends Element {
  static create({ label, options = {} }: { label: string; options: Options }): DropdownElement {
    const el = new DropdownElement({
      label,
      description: options.description,
      mandatory: options.mandatory || options.locked,
    })
    if (options.locked) {
      el.lock()
    }

    return el
  }

  /**
   * Get the name of the element
   */
  getName(): string {
    return i18n.t('forms.modals.elements.dropdown')
  }

  /**
   * Get the type of the element
   */
  getType(): FormsElementTypeEnum {
    return 'dropdownsingle'
  }

  /**
   * Get the icon for the element
   */
  getIcon(): ForwardRefExoticComponent<SVGProps<SVGSVGElement>> {
    return ChevronDown
  }

  /**
   * Validate the element's configuration
   */
  validate(validateCorrectOption: boolean): Record<string, string> {
    const errors = super.validate(validateCorrectOption)

    const options = this.options
    if (options.length < 2) {
      errors.options = i18n.t('forms.editor.validation.min_two_options')
    } else if (options.some((option) => !option.label.trim())) {
      errors.options = i18n.t('forms.editor.validation.option_label_required')
    } else if (validateCorrectOption && !options.some((option) => option.isCorrect)) {
      errors.options = i18n.t('forms.editor.validation.no_correct_option')
    } else if (validateCorrectOption && options.filter((option) => option.isCorrect).length > 1) {
      errors.options = i18n.t('forms.editor.validation.only_one_correct_option')
    }

    return errors
  }

  /**
   * Get a React component for editing this element
   */
  getEditorComponent(
    onChange: (element: Element) => void,
    enableCorrectConfiguration: boolean
  ): React.ReactNode {
    return (
      <ChoiceElementEditor
        element={this}
        onChange={onChange}
        enableCorrectConfiguration={enableCorrectConfiguration}
      />
    )
  }

  getPreviewComponent(disabled?: boolean, showCorrectAnswerIcons?: boolean): React.ReactNode {
    return (
      <DropdownElementQuestion
        element={this}
        disabled={disabled}
        showCorrectAnswerIcons={showCorrectAnswerIcons}
      />
    )
  }

  getQuestionComponent(onChange: (answer: Answer) => void): React.ReactNode {
    return (
      <DropdownElementQuestion element={this} onChange={onChange} showCorrectAnswerIcons={false} />
    )
  }

  configureParams(): elementParam[] {
    return [Element.booleanParam('randomizeOptions', 'Randomize')]
  }
}
